import { useState, useEffect, useCallback } from "react";
import { BookingType } from "../AddBookingV2";
import apiService from "../../../../service/Api";
import type { Package, AdditionalServicesCategories } from "../../../../types/package";

interface ServiceResponse {
  id: number;
  name: string;
  description: string | null;
  price: string | null;
  service_category: string;
  service_category_display: string;
  created_at: string;
  updated_at: string;
}

interface PaginatedServiceResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ServiceResponse[];
}

interface PackageBaseService {
  id: number;
  name: string;
  price?: string | number;
}

interface PaginatedPackageResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    id: number;
    name: string;
    package_type: string;
    base_price: string;
    description: string;
    created_at: string;
    updated_at: string;
    company: number;
    base_services: PackageBaseService[];
  }[];
}

const BOOKING_TYPE_TO_SERVICE_TYPE: Record<BookingType, string> = {
  funeral: "FUNERAL",
  funeral_home: "FUNERAL_HOME",
  conference: "CONFERENCE",
  church: "CHURCH",
  event: "EVENT",
  other: "OTHER",
};

const isPaginatedPackageResponse = (data: unknown): data is PaginatedPackageResponse => {
  return (
    typeof data === "object" &&
    data !== null &&
    "count" in data &&
    "results" in data &&
    Array.isArray((data as PaginatedPackageResponse).results)
  );
};

const isPaginatedServiceResponse = (data: unknown): data is PaginatedServiceResponse => {
  return (
    typeof data === "object" &&
    data !== null &&
    "count" in data &&
    "results" in data &&
    Array.isArray((data as PaginatedServiceResponse).results)
  );
};

export function usePackagesAndServices(bookingType: BookingType) {
  const [packages, setPackages] = useState<Package[]>([]);
  const [additionalServicesConfig, setAdditionalServicesConfig] = useState<AdditionalServicesCategories>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const serviceType = BOOKING_TYPE_TO_SERVICE_TYPE[bookingType];

  const fetchAllPackages = useCallback(async (url: string) => {
    let results: PaginatedPackageResponse["results"] = [];
    let nextUrl: string | null = url;
    while (nextUrl) {
      const response: { data: unknown } = await apiService.get(nextUrl);
      const data: unknown = response.data;
      if (!isPaginatedPackageResponse(data)) throw new Error("Invalid package data format");
      results = [...results, ...data.results];
      nextUrl = data.next ? new URL(data.next).pathname + new URL(data.next).search : null;
    }
    return results;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const serviceTypeMap: Record<string, string | string[]> = {
          FUNERAL: ["Funeral"],
          FUNERAL_HOME: "FuneralHome",
          CHURCH: ["Church"],
          CONFERENCE: "Conference",
          EVENT: "Event",
          OTHER: "Other",
        };

        const selectedType = serviceTypeMap[serviceType] || "";

        let fetchedPackages: Package[];
        if (Array.isArray(selectedType)) {
          const responses = await Promise.all(
            selectedType.map((type) =>
              fetchAllPackages(`/api/packages/package/?package_type=${type}&page_size=20`)
            )
          );
          fetchedPackages = responses.flat().map((item) => ({
            id: item.id,
            name: item.name,
            price: parseFloat(item.base_price) || 0,
            contents: item.description
              ? item.description.split("\n").filter((line) => line.trim())
              : [],
            package_type: item.package_type || "Funeral",
            base_services: item.base_services,
          }));
        } else {
          const items = await fetchAllPackages(
            `/api/packages/package/?package_type=${selectedType}&page_size=20`
          );
          fetchedPackages = items.map((item) => ({
            id: item.id,
            name: item.name,
            price: parseFloat(item.base_price) || 0,
            contents: item.description
              ? item.description.split("\n").filter((line) => line.trim())
              : [],
            package_type: item.package_type || String(selectedType),
            base_services: item.base_services,
          }));
        }

        setPackages(fetchedPackages);

        // Fetch additional services
        let allServiceResults: ServiceResponse[] = [];
        let nextUrl: string | null = "/api/packages/package-service/";
        while (nextUrl) {
          const serviceResponse: { data: unknown } = await apiService.get(nextUrl);
          const serviceData: unknown = serviceResponse.data;
          if (!isPaginatedServiceResponse(serviceData)) throw new Error("Invalid service data format");
          allServiceResults = [...allServiceResults, ...serviceData.results];
          nextUrl = serviceData.next
            ? new URL(serviceData.next).pathname + new URL(serviceData.next).search
            : null;
        }

        const groupedServices: AdditionalServicesCategories = allServiceResults.reduce(
          (acc, item) => {
            const category = item.service_category || "Other";
            if (!acc[category]) acc[category] = [];
            acc[category].push({
              value: item.id.toString(),
              label: item.name,
              price: parseFloat(item.price ?? "0") || 0,
              description: item.description || undefined,
            });
            return acc;
          },
          {} as AdditionalServicesCategories
        );

        setAdditionalServicesConfig(groupedServices);
      } catch (err: unknown) {
        setError((err as { message?: string })?.message || "Failed to load packages or services");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [serviceType, fetchAllPackages]);

  return { packages, additionalServicesConfig, loading, error };
}